import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ResizeObserver from 'resize-observer-polyfill';

// https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node

export interface Bounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface UseMeasureCallbackProps<T extends Element> {
  bounds: Bounds;
  elementRef: MutableRefObject<T | null>;
}

export interface UseMeasureCallback<T extends Element> {
  (props: UseMeasureCallbackProps<T>): void;
}

interface UseMeasureProps<T extends Element> {
  callback: UseMeasureCallback<T>;
}

interface UseMeasureReturn<T extends Element> {
  ref: ElementReference<T>;
  bounds: Bounds;
}

export type ElementReference<T extends Element> = (node: T) => void;

export function useMeasure<T extends Element>(
  props?: UseMeasureProps<T>,
): UseMeasureReturn<T> {
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });

  const elementRef = useRef<T | null>(null);

  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const callback = props?.callback;

  const resizeObserver = useMemo(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    const resizeObserver = new ResizeObserver((entries: any) => {
      const entry = entries[0];

      if (entry && entry.contentRect) {
        setBounds(entry.contentRect);

        callback && callback({ bounds: entry.contentRect, elementRef });
      }
    });

    resizeObserverRef.current = resizeObserver;

    return resizeObserver;
  }, [callback]);

  const ref = useCallback(
    (node: any) => {
      if (node !== null) {
        elementRef.current = node;

        resizeObserver.disconnect();
        resizeObserver.observe(node);
      }
    },
    [resizeObserver],
  );

  useEffect(() => {
    return () => resizeObserver.disconnect();
  }, [resizeObserver]);

  return { ref, bounds };
}
